import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Star, Quote, MapPin, Phone, ArrowRight, Filter } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import SEO from "../components/SEO";
import CalculatorCallout from "../components/CalculatorCallout";
import { reviews, reviewStats } from "../data/reviews";
import { businessInfo } from "../mock";
import { localBusinessSchema, breadcrumbSchema } from "../utils/schemas";

const StarRating = ({ rating, size = "default" }) => {
  const sizes = { sm: "h-3.5 w-3.5", default: "h-4 w-4", lg: "h-5 w-5" };
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`${sizes[size]} ${
            i < rating
              ? "fill-[#E11D26] text-[#E11D26]"
              : "text-white/20"
          }`}
        />
      ))}
    </div>
  );
};

const ReviewsPage = () => {
  const [filter, setFilter] = useState("All");

  const claimTypes = ["All", ...new Set(reviews.map((r) => r.claimType))];
  const filtered =
    filter === "All" ? reviews : reviews.filter((r) => r.claimType === filter);

  const featured = filtered.filter((r) => r.featured);
  const others = filtered.filter((r) => !r.featured);

  const schemas = [
    localBusinessSchema(),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Reviews", path: "/reviews" },
    ]),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title="Reviews | Claim Beast Public Adjusters Tampa Bay | 5★"
        description="Read verified 5-star Google reviews from Tampa Bay homeowners who chose Claim Beast Public Adjusters in Riverview, FL."
        path="/reviews"
        keywords="claim beast reviews, public adjuster reviews tampa, public adjuster reviews florida, claim beast testimonials, riverview public adjuster reviews"
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs items={[{ name: "Reviews", path: "/reviews" }]} />

      {/* Hero / Stats */}
      <section className="relative bg-black py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(225,29,38,0.12),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-[2px] w-10 bg-[#E11D26]" />
            <span className="text-[#E11D26] text-xs sm:text-sm font-bold tracking-[0.25em] uppercase">
              Tampa Bay Homeowners Speak
            </span>
          </div>
          <h1
            className="text-white text-5xl sm:text-6xl md:text-7xl font-black uppercase leading-[0.92]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            <span className="text-[#E11D26]">Real</span> Clients.
            <br />
            <span className="text-[#E11D26]">Real</span> Results.
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl">
            See what Riverview, Tampa, Brandon and Tampa Bay homeowners are
            saying about working with Joe &amp; Victoria at Claim Beast.
          </p>

          {/* Aggregate stats card */}
          <div className="mt-9 inline-flex items-center gap-6 bg-[#0F0F0F] border border-white/10 px-6 py-4">
            <div>
              <div
                className="text-[#E11D26] text-4xl sm:text-5xl font-black leading-none"
                style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
              >
                {reviewStats.averageRating}
              </div>
              <StarRating rating={5} size="default" />
            </div>
            <div className="h-12 w-px bg-white/15" />
            <div>
              <div className="text-white font-black text-2xl">
                {reviewStats.totalReviews}+
              </div>
              <div className="text-white/65 text-xs uppercase tracking-wider">
                Verified Reviews
              </div>
            </div>
            <div className="h-12 w-px bg-white/15 hidden sm:block" />
            <div className="hidden sm:block">
              <div className="text-white font-black text-2xl">
                {reviewStats.fiveStarPercentage}%
              </div>
              <div className="text-white/65 text-xs uppercase tracking-wider">
                5-Star
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="bg-[#0A0A0A] border-y border-white/10 sticky top-[120px] z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 overflow-x-auto">
          <Filter className="h-4 w-4 text-[#E11D26] shrink-0" />
          <span className="text-white/65 text-xs font-bold tracking-[0.2em] uppercase shrink-0">
            Filter:
          </span>
          {claimTypes.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`shrink-0 text-xs font-bold tracking-wider uppercase px-3 py-1.5 transition-colors ${
                filter === c
                  ? "bg-[#E11D26] text-white"
                  : "bg-[#0F0F0F] text-white/70 hover:text-white border border-white/10"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Featured reviews */}
      {featured.length > 0 && (
        <section className="bg-[#0A0A0A] py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2
              className="text-3xl font-black text-white uppercase mb-7"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Featured <span className="text-[#E11D26]">Wins</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {featured.map((r) => (
                <ReviewCard key={r.id} review={r} featured />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All reviews grid (only show if there are non-featured reviews) */}
      {others.length > 0 && (
        <section className="bg-black py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2
              className="text-3xl font-black text-white uppercase mb-7"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              {filter === "All" ? "More Reviews" : `${filter} Reviews`}
              {filter !== "All" && (
                <span className="text-white/40 text-base font-bold ml-3">
                  ({others.length + featured.length})
                </span>
              )}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {others.map((r) => (
                <ReviewCard key={r.id} review={r} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Empty state when filter returns nothing */}
      {filter !== "All" && filtered.length === 0 && (
        <section className="bg-black py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <p className="text-white/60">
              No reviews yet for &quot;{filter}&quot;. Check back soon.
            </p>
          </div>
        </section>
      )}

      {/* Submit CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3
              className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Worked with us? Leave a review.
            </h3>
            <p className="mt-2 text-white/90">
              Help your Tampa Bay neighbors find honest claim help.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="https://www.google.com/search?q=Claim+Beast+Public+Adjusters+Riverview+FL"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-black text-white hover:bg-[#0A0A0A] font-bold uppercase tracking-wider px-6 py-4 transition-colors text-sm"
            >
              Leave a Google Review
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={`tel:${businessInfo.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-6 py-4 transition-colors text-sm"
            >
              <Phone className="h-4 w-4" />
              {businessInfo.phone}
            </a>
          </div>
        </div>
      </section>

      <CalculatorCallout
        eyebrow="These Results Aren't Lucky"
        headline="Want To Know What We'd Recover For You?"
        body="Every review above started with the same first step: an honest look at the damage and a defensible number to fight for. Run yours in 60 seconds — then we'll reach out to walk through the next steps."
        ctaLabel="See My Estimate"
      />

      <Footer />
    </div>
  );
};

const ReviewCard = ({ review, featured }) => (
  <article
    className={`relative bg-[#0F0F0F] border ${
      featured ? "border-[#E11D26]/40" : "border-white/10"
    } p-6 sm:p-7 hover:border-[#E11D26]/60 transition-colors`}
  >
    <Quote className="absolute top-4 right-4 h-8 w-8 text-[#E11D26]/15" />
    <div className="flex items-center justify-between gap-3 mb-4">
      <StarRating rating={review.rating} />
      <span className="text-[10px] text-[#E11D26] font-bold tracking-[0.2em] uppercase bg-[#E11D26]/10 border border-[#E11D26]/30 px-2.5 py-1">
        {review.claimType}
      </span>
    </div>
    <p className="text-white/85 leading-relaxed text-[15px]">&ldquo;{review.text}&rdquo;</p>
    <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
      <div>
        <div className="text-white font-bold">{review.name}</div>
        <div className="text-white/55 text-xs flex items-center gap-1 mt-0.5">
          <MapPin className="h-3 w-3 text-[#E11D26]" />
          {review.location}
        </div>
      </div>
      <div className="text-white/45 text-xs">
        {new Date(review.date).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        })}
      </div>
    </div>
  </article>
);

export default ReviewsPage;
